import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ApiHttpError } from "@atlas/sdk";
import { Button, CurrencyField, FormActions, FormGrid, NumberField, Pill, TextareaField, TextField } from "@atlas/ui/web";
import { CET_MINIMO_AM, avisoCoerenciaParcela, cetAnual } from "@atlas/domain";
import { atlas } from "../../../lib/sdk";
import { buildSimplePdf, downloadPdf } from "../../../lib/pdf";
import {
  PRODUTO_LABEL,
  STATUS_LABEL,
  contratoToProposta,
  fmtBRL,
  fmtDateTime,
  getBancoPerfil,
  getProposta,
  statusPill,
  travaInfo,
  type BancoProposta,
} from "../../../lib/banco-propostas";
import { TotalEmFolha, ValorSolicitado } from "../../../components/ValorContrato";
import { NAO_REGISTRADO, rotuloSubOrganizacao } from "../../../lib/registro-agente";
import { InformarDadosLegadoModal } from "../../../components/InformarDadosLegadoModal";
import { rotuloInformadoPor } from "../../../lib/dados-informados";

export function BancoPropostaDetalhe() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [version, setVersion] = useState(0);
  // Proposta pode vir de duas fontes: (a) SEED local + overlay (fluxo antigo,
  // hoje vazio) ou (b) contratos do backend (fluxo real). Primeiro tenta o
  // local; se nao achar, procura no atlas.banco.contratos() pelo ADF.
  const local = getProposta(id);
  // Sempre busca a lista do backend — mesmo com "local", o status da proposta
  // pode mudar por acao do proprio banco (aprovar/recusar) e queremos ver o
  // reflexo imediatamente. O invalidate depois da mutation dispara o refetch.
  const apiQ = useQuery({
    queryKey: ["banco", "propostas-api"],
    queryFn: () => atlas.banco.contratos(),
    refetchInterval: 10_000,
  });
  const fromApi = apiQ.data?.contratos.find((c) => c.adf === id);
  // fromApi tem PRIORIDADE — reflete a decisao ja persistida (aprovada/recusada
  // pelo backend). O seed local so entra como fallback pra propostas de
  // demonstracao que nunca subiram pra API.
  const proposta: BancoProposta | undefined = fromApi ? contratoToProposta(fromApi) : local;
  // Enquanto a query da API ainda esta carregando, nao mostra "nao encontrada".
  const carregandoApi = !local && apiQ.isPending;
  // Erro de carregamento e' DIFERENTE de "nao encontrada": sem distinguir, uma
  // falha de rede em deep-link/refresh (sem cache da lista) mostrava "Proposta
  // nao encontrada" e escondia o erro real. Bug reportado 30/07/2026.
  const erroApi = !local && apiQ.isError;
  const perfil = getBancoPerfil();
  // Modais: "recusar" (motivo), "anexar" (upload de contrato), "editar"
  // (contra-proposta) e "informar" (dados do legado — feature 012).
  const [modal, setModal] = useState<null | "recusar" | "anexar" | "editar" | "informar">(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const qc = useQueryClient();
  // Hooks TEM QUE ficar antes de qualquer early return (regras dos hooks) —
  // caso contrario, quando a proposta troca de "undefined" (loading) pra
  // definida, o React ve um numero diferente de hooks e lanca #310.
  const propostaId = proposta?.idUnico ?? "";
  const refresh = () => setVersion((v) => v + 1);
  // Aprovar/recusar batem no BACKEND — persiste e o servidor ve a mudanca.
  const decidir = useMutation({
    mutationFn: (v: { acao: "aprovar" | "cancelar"; motivo?: string }) =>
      atlas.banco.acao(propostaId, v.acao, v.motivo ? { motivo: v.motivo } : undefined),
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ["banco", "propostas-api"] });
      qc.invalidateQueries({ queryKey: ["servidor", "propostas"] });
      refresh();
    },
    onError: (err) => {
      setSaveError((err as Error).message || "Falha ao salvar a decisão no servidor.");
    },
  });

  if (!proposta) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ color: erroApi ? "var(--danger-500)" : "var(--text-muted)" }}>
          {carregandoApi
            ? "Carregando proposta…"
            : erroApi
              ? "Não foi possível carregar a proposta. Verifique a conexão e tente novamente."
              : "Proposta não encontrada."}
        </div>
        {!carregandoApi ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {erroApi ? (
              <Button variant="ghost" onClick={() => apiQ.refetch()}>Tentar novamente</Button>
            ) : null}
            <Button variant="ghost" onClick={() => nav("/banco/propostas")}>← Voltar para a fila</Button>
          </div>
        ) : null}
      </div>
    );
  }

  const trava = travaInfo(proposta);

  const aprovar = () => {
    if (decidir.isPending) return;
    decidir.mutate({ acao: "aprovar" });
  };

  return (
    <div key={version} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <Button variant="ghost" size="sm" onClick={() => nav("/banco/propostas")}>← Voltar para a fila</Button>
      </div>

      {saveError ? (
        <div
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: "1px solid var(--danger-500)",
            background: "color-mix(in srgb, var(--danger-500) 10%, transparent)",
            fontSize: ".88rem",
          }}
        >
          {saveError}
        </div>
      ) : null}

      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Análise de proposta
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem", fontFamily: "var(--font-mono)" }}>{proposta.idUnico}</h1>
          <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 12 }}>
            <Pill variant={statusPill(proposta.status)}>{STATUS_LABEL[proposta.status]}</Pill>
            {trava ? (
              <span style={{ fontSize: 13, color: trava.expirada ? "var(--danger-500)" : trava.urgente ? "var(--gold-500)" : "var(--text-muted)" }}>
                {trava.expirada ? "Trava expirada" : `Trava restante: ${trava.label}`}
              </span>
            ) : null}
          </div>
        </div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <InfoCard title="Dados do servidor">
          <Row label="Nome" value={proposta.nome} />
          <Row label="CPF" value={proposta.cpfMasked} mono />
          <Row label="Matrícula" value={proposta.matricula} mono />
          <Row label="Convênio" value={proposta.convenio} />
          <Row label="Vínculo" value={proposta.vinculo} />
          <Row label="Situação funcional" value={proposta.situacaoFuncional} />
          {proposta.telefoneServidor ? (
            <TelefoneRow telefone={proposta.telefoneServidor} />
          ) : (
            <Row label="Celular" value="—" />
          )}
        </InfoCard>

        <InfoCard title="Dados da operação">
          <Row label="Produto" value={PRODUTO_LABEL[proposta.produto]} />
          {/* Valor solicitado e total em folha sao grandezas diferentes
              (feature 012): no legado sem informe o primeiro e' "—" com o selo,
              e a taxa tambem — a Atlas nao tem a cedula. */}
          <Row
            label="Valor solicitado"
            value={
              <ValorSolicitado
                contrato={{
                  valorParcela: proposta.parcela, totalParcelas: proposta.parcelas, valorFinanciado: proposta.valor,
                  dadosFinanceirosConhecidos: proposta.dadosFinanceirosConhecidos, origem: proposta.origem, dadosInformados: proposta.dadosInformados,
                }}
                formatar={fmtBRL}
              />
            }
          />
          <Row label="Parcelas" value={`${proposta.parcelas}x de ${fmtBRL(proposta.parcela)}`} />
          <Row label="Total em folha" value={<TotalEmFolha contrato={{ valorParcela: proposta.parcela, totalParcelas: proposta.parcelas }} formatar={fmtBRL} />} />
          <Row label="Taxa a.m." value={proposta.dadosFinanceirosConhecidos === false ? "—" : `${proposta.taxaAm.toFixed(2)}%`} />
          <Row label="Recebida em" value={fmtDateTime(proposta.criadaEm)} />
        </InfoCard>

        {/* Quem registrou pelo banco e de qual sub organizacao — o que distingue
            a filial da organizacao principal. Contrato anterior a 01/10/2026
            nao tem o registro e diz isso, em vez de presumir a matriz. */}
        <InfoCard title="Origem do registro">
          <Row label="Registrado por" value={proposta.registradoPor?.usuarioNome ?? NAO_REGISTRADO} />
          <Row label="Sub organização" value={rotuloSubOrganizacao(proposta.registradoPor)} />
          {proposta.registradoPor ? <Row label="Em" value={fmtDateTime(proposta.registradoPor.em)} /> : null}
        </InfoCard>

        {proposta.origem === "legado" ? (
          <InfoCard title="Contrato legado">
            <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
              Esta dívida já estava na folha quando a Atlas chegou. A parcela e o prazo vêm da folha;
              a Atlas não tem a cédula — quem a tem é a consignatária.
            </div>
            {proposta.dadosInformados ? (
              <div style={{ fontSize: 13 }}>
                Valor solicitado e taxa {rotuloInformadoPor(proposta.dadosInformados)}
                {proposta.dadosInformados.porNome ? ` (${proposta.dadosInformados.porNome})` : ""}.
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--gold-500)" }}>
                Valor solicitado e taxa ainda não informados.
              </div>
            )}
            {perfil.perms.aprovacao ? (
              <div>
                <Button variant="primary" size="sm" onClick={() => setModal("informar")}>
                  {proposta.dadosInformados ? "Corrigir dados do contrato" : "Informar dados do contrato"}
                </Button>
              </div>
            ) : null}
          </InfoCard>
        ) : null}

        <InfoCard title="Margem por matrícula">
          {/* LGPD: banco NAO ve salario liquido do servidor — mesma politica
              do /portal/banco/margem/buscar. Mostra so margens calculadas. */}
          <Row label="Margem comprometida (esta op.)" value={fmtBRL(proposta.margemComprometida)} />
          <Row label="Margem disponível" value={fmtBRL(proposta.margemDisponivel)} />
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-dim)" }}>
            A averbadora garante a identificação do servidor e a trava de margem — a análise de crédito é da consignatária.
          </div>
        </InfoCard>
      </div>

      {proposta.editadaPeloBanco ? (
        <InfoCard title="Valores editados pela consignatária">
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>
            Você ajustou esta proposta (negociação offline). O servidor vê estes valores e a observação abaixo.
          </div>
          <Row label="Valor (antes → agora)" value={`${fmtBRL(proposta.editadaPeloBanco.valorOriginal)} → ${fmtBRL(proposta.valor)}`} />
          <Row label="Parcelas (antes → agora)" value={`${proposta.editadaPeloBanco.parcelasOriginal}x → ${proposta.parcelas}x`} />
          <Row label="Taxa a.m. (antes → agora)" value={`${(proposta.editadaPeloBanco.taxaAmOriginal * 100).toFixed(2)}% → ${proposta.taxaAm.toFixed(2)}%`} />
          {proposta.editadaPeloBanco.motivo ? (
            <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 8%, transparent)", border: "1px solid var(--gold-500)", fontSize: 13 }}>
              <b>Sua observação:</b> {proposta.editadaPeloBanco.motivo}
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 12, color: "var(--text-dim)" }}>Nenhuma observação escrita nesta edição.</div>
          )}
        </InfoCard>
      ) : null}

      <InfoCard title="Contratos ativos relacionados">
        {proposta.contratosAtivos.length === 0 ? (
          <div style={{ color: "var(--text-dim)", fontSize: 13 }}>Nenhum contrato ativo relacionado a esta matrícula.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {proposta.contratosAtivos.map((c) => (
              <div
                key={c.idUnico}
                style={{
                  display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8,
                  padding: "10px 12px", background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 10,
                }}
              >
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{c.idUnico}</span>
                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{c.banco}</span>
                <span style={{ fontSize: 13 }}>{fmtBRL(c.valorParcela)} · {c.parcelasRestantes}x restantes</span>
                <Pill variant="emdia">{c.situacao}</Pill>
              </div>
            ))}
          </div>
        )}
      </InfoCard>

      {/* Analise de risco interna ao banco (Passo 5) */}
      {proposta.risco ? <RiscoPanel risco={proposta.risco} /> : null}

      {/* Proximo passo — dirigido por status (Passos 4, 6, 7) */}
      <div
        style={{
          display: "flex", flexDirection: "column", gap: 12,
          padding: 16, background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12,
        }}
      >
        <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
          Próximo passo
        </div>
        <NextStep
          proposta={proposta}
          podeAgir={perfil.perms.aprovacao}
          perfilNome={perfil.nome}
          submitting={decidir.isPending}
          onAprovar={aprovar}
          onAnexarContrato={() => setModal("anexar")}
          onRecusar={() => setModal("recusar")}
          onEditar={() => setModal("editar")}
        />
      </div>

      {proposta.motivoRecusa ? (
        <div style={{ fontSize: 13, color: "var(--danger-500)" }}>Motivo da recusa: {proposta.motivoRecusa}</div>
      ) : null}
      {proposta.observacao ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Observação / info solicitada: {proposta.observacao}</div>
      ) : null}

      {modal === "recusar" ? (
        <DecisaoModal
          proposta={proposta}
          submitting={decidir.isPending}
          onClose={() => setModal(null)}
          onConfirm={(motivo) => {
            decidir.mutate(
              { acao: "cancelar", motivo },
              { onSuccess: () => setModal(null) },
            );
          }}
        />
      ) : modal === "anexar" ? (
        <AnexarContratoModal
          proposta={proposta}
          onClose={() => setModal(null)}
          onUploaded={() => {
            qc.invalidateQueries({ queryKey: ["banco", "propostas-api"] });
            refresh();
          }}
        />
      ) : modal === "editar" ? (
        <EditarPropostaModal
          proposta={proposta}
          onClose={() => setModal(null)}
          onSalvo={() => {
            qc.invalidateQueries({ queryKey: ["banco", "propostas-api"] });
            refresh();
            setModal(null);
          }}
        />
      ) : modal === "informar" ? (
        <InformarDadosLegadoModal
          adf={proposta.idUnico}
          nome={proposta.nome}
          valorParcela={proposta.parcela}
          totalParcelas={proposta.parcelas}
          atual={proposta.dadosInformados ?? null}
          papel="banco"
          onClose={() => setModal(null)}
          onSalvo={() => {
            qc.invalidateQueries({ queryKey: ["banco", "propostas-api"] });
            qc.invalidateQueries({ queryKey: ["banco", "contratos-api"] });
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

function NextStep({
  proposta,
  podeAgir,
  perfilNome,
  submitting,
  onAprovar,
  onAnexarContrato,
  onRecusar,
  onEditar,
}: {
  proposta: BancoProposta;
  podeAgir: boolean;
  perfilNome: string;
  submitting: boolean;
  onAprovar: () => void;
  onAnexarContrato: () => void;
  onRecusar: () => void;
  onEditar: () => void;
}) {
  const s = proposta.status;
  const acionavel = ["recebida", "em_analise", "mais_info", "aprovada", "aguardando_formalizacao", "formalizada"].includes(s);

  if (acionavel && !podeAgir) {
    return (
      <span style={{ color: "var(--text-muted)", fontSize: 14 }}>
        Seu perfil <strong>{perfilNome}</strong> tem apenas permissão de consulta. Ações indisponíveis nesta etapa.
      </span>
    );
  }

  // Fluxo pedido pelo cliente:
  //   1. Proposta em analise: banco ANEXA o contrato assinado primeiro.
  //   2. Depois de anexar, libera o botao "Aprovar proposta".
  //   3. Depois de aprovada, so tem "Atualizar contrato" — nunca excluir.
  //      Cada atualizacao arquiva a versao antiga no historico (nao apaga).
  if (s === "recebida" || s === "em_analise" || s === "mais_info") {
    const jaAnexado = !!proposta.ccbKey;
    // Cliente pediu 29/07/2026: o anexo do contrato deixa de ser pre-requisito
    // pra aprovar. O banco pode aprovar direto e anexar o contrato depois
    // (antes da averbacao efetiva). O aviso continua caso ainda nao tenha.
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontSize: 14, color: "var(--text-muted)" }}>
          {jaAnexado
            ? "Contrato anexado. Voce pode aprovar a proposta ou atualizar o arquivo antes."
            : "Analise os dados do servidor e a margem. Voce pode aprovar agora — o contrato assinado pode ser anexado depois, antes da averbacao."}
        </div>
        {jaAnexado ? <CcbAnexadoView proposta={proposta} onAtualizar={onAnexarContrato} /> : null}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <Button variant="primary" onClick={onAnexarContrato} disabled={submitting}>
            {jaAnexado ? "Atualizar contrato →" : "Anexar contrato →"}
          </Button>
          {jaAnexado ? null : <BaixarModeloCcb proposta={proposta} />}
          <Button variant="success" onClick={onAprovar} disabled={submitting}>
            {submitting ? "Aprovando..." : "Aprovar proposta →"}
          </Button>
          <Button variant="ghost" onClick={onEditar} disabled={submitting}>
            Editar valores
          </Button>
          <div style={{ marginLeft: "auto" }}>
            <Button variant="ghost" onClick={onRecusar} disabled={submitting}>Recusar</Button>
          </div>
        </div>
      </div>
    );
  }

  // Aprovada: contrato pode ou nao estar anexado. Cliente pediu 30/07/2026:
  // quando aprovou SEM anexar contrato, mostrar aviso + botao "Adicionar
  // contrato" em vez do card generico "Contrato atual/Atualizar" (que ficava
  // rotulado errado apontando pra um arquivo inexistente).
  if (s === "aprovada" || s === "aguardando_formalizacao" || s === "formalizada") {
    const jaAnexado = !!proposta.ccbKey;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {jaAnexado ? (
          <>
            <div style={{ fontSize: 14, color: "var(--text-muted)" }}>
              Proposta <strong>aprovada</strong>. O contrato ja esta salvo aqui. Se precisar corrigir,
              voce pode atualizar por uma nova versao — a antiga fica arquivada no historico
              e nunca e apagada.
            </div>
            <CcbAnexadoView proposta={proposta} onAtualizar={onAnexarContrato} />
          </>
        ) : (
          <>
            <div
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid color-mix(in srgb, var(--gold-500) 50%, transparent)",
                background: "color-mix(in srgb, var(--gold-500) 10%, transparent)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.5 }}>
                Proposta <strong>aprovada</strong>, mas o contrato assinado <b>ainda nao foi anexado</b>.
                A averbacao vai seguir o fluxo, mas anexar o CCB deixa o registro
                completo e evita bloqueio quando a prefeitura exigir CCB.
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <Button variant="primary" onClick={onAnexarContrato} disabled={submitting}>
                + Adicionar contrato
              </Button>
              <BaixarModeloCcb proposta={proposta} />
            </div>
          </>
        )}
      </div>
    );
  }

  // averbada — mostra o contrato se anexado; senao, AVISA e deixa anexar (o
  // servidor so consegue baixar o CCB depois de anexado, e e' notificado ao
  // anexar). Antes o caso "sem CCB" era silencioso. Cliente 31/07/2026.
  if (s === "averbada") {
    const jaAnexado = !!proposta.ccbKey;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <span style={{ fontSize: 14, color: "var(--success)" }}>
          Contrato averbado. Formalização concluída — a averbação está registrada em folha.
        </span>
        {jaAnexado ? (
          <CcbAnexadoView proposta={proposta} onAtualizar={onAnexarContrato} />
        ) : (
          <>
            <div
              style={{
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid color-mix(in srgb, var(--gold-500) 50%, transparent)",
                background: "color-mix(in srgb, var(--gold-500) 10%, transparent)",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <span style={{ fontSize: 18 }}>⚠️</span>
              <div style={{ fontSize: 14, color: "var(--text)", lineHeight: 1.5 }}>
                Averbação concluída, mas o <b>contrato assinado ainda não foi anexado</b>.
                Anexe o CCB para o servidor poder baixá-lo — ele é notificado assim que você anexar.
              </div>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <Button variant="primary" onClick={onAnexarContrato} disabled={submitting}>
                + Adicionar contrato
              </Button>
              <BaixarModeloCcb proposta={proposta} />
            </div>
          </>
        )}
      </div>
    );
  }
  // recusada / expirada — sem botoes.
  return (
    <span style={{ color: "var(--text-muted)", fontSize: 14 }}>
      Proposta em <strong>{STATUS_LABEL[s]}</strong> — sem proximos passos.
    </span>
  );
}

function RiscoPanel({ risco }: { risco: NonNullable<BancoProposta["risco"]> }) {
  const recCor = risco.recomendacao === "aprovar" ? "var(--success)" : risco.recomendacao === "negar" ? "var(--danger-500)" : "var(--gold-500)";
  const recLabel = risco.recomendacao === "aprovar" ? "Aprovar" : risco.recomendacao === "negar" ? "Negar" : "Revisar";
  return (
    <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>
          Análise de risco (interna da consignatária)
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: recCor }}>Recomendação: {recLabel}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <Metric label="Score interno" value={`${risco.scoreInterno}`} />
        <Metric label="Serasa" value={`${risco.bureauSerasa}`} />
        <Metric label="SPC" value={risco.bureauSpc === "sem_restricao" ? "Sem restrição" : "Com restrição"} warn={risco.bureauSpc === "com_restricao"} />
        <Metric label="Comprom. de renda" value={`${(risco.comprometimentoRenda * 100).toFixed(0)}%`} warn={risco.comprometimentoRenda > 0.4} />
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: "var(--text-dim)" }}>
        A averbadora não faz análise de crédito — ela apenas garante a identificação do servidor. O risco é integralmente da consignatária.
      </div>
    </div>
  );
}

function Metric({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div style={{ background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4, color: warn ? "var(--gold-500)" : "var(--text)" }}>{value}</div>
    </div>
  );
}

function DecisaoModal({
  proposta,
  submitting,
  onClose,
  onConfirm,
}: {
  proposta: BancoProposta;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (motivo: string | undefined) => void;
}) {
  const [texto, setTexto] = useState("");
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>Recusar proposta</h3>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {proposta.nome} · <code>{proposta.idUnico}</code>
        </div>
        <TextareaField
          label="Motivo da recusa (opcional)"
          hint="Se preenchido, o servidor verá o motivo. Deixe em branco para recusar sem justificativa."
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={4}
          maxLength={300}
        />
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>Voltar</Button>
          <Button type="button" onClick={() => onConfirm(texto.trim() || undefined)} disabled={submitting}>
            {submitting ? "Recusando..." : "Confirmar recusa"}
          </Button>
        </FormActions>
      </div>
    </div>
  );
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--bg-elev)", border: "1px solid var(--border-strong)", borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 12 }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

/** Row de celular do servidor com atalho "Ligar" (`tel:`) e "Copiar". O banco
 *  precisa entrar em contato pra tocar a formalizacao offline. */
function TelefoneRow({ telefone }: { telefone: string }) {
  const digits = telefone.replace(/\D/g, "");
  const fmt = digits.length === 11
    ? `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
    : digits.length === 10
      ? `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
      : telefone;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14, alignItems: "center" }}>
      <span style={{ color: "var(--text-muted)" }}>Celular</span>
      <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <a
          href={`tel:${digits}`}
          style={{ fontWeight: 600, fontFamily: "var(--font-mono)", color: "var(--accent)", textDecoration: "none" }}
        >
          {fmt}
        </a>
        <button
          type="button"
          onClick={() => { void navigator.clipboard?.writeText(digits); }}
          title="Copiar"
          style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)", borderRadius: 6, padding: "2px 8px", fontSize: 11, cursor: "pointer" }}
        >
          Copiar
        </button>
      </span>
    </div>
  );
}

/** Gera um contrato CCB modelo em PDF pro banco imprimir e coletar a assinatura
 *  do servidor PRESENCIALMENTE. Gera client-side (buildSimplePdf) pra nao
 *  depender de auth no endpoint /comprovante.pdf do backend — o browser nao
 *  manda o Bearer token quando <a href> abre a URL direto (401). */
function baixarContratoModelo(proposta: BancoProposta): void {
  const hoje = new Date().toLocaleDateString("pt-BR");
  const bytes = buildSimplePdf("CEDULA DE CREDITO BANCARIO (CCB) - MODELO", [
    { text: `Proposta: ${proposta.idUnico}`, bold: true },
    { text: `Emitida em: ${hoje}` },
    "",
    { text: "1. IDENTIFICACAO DO EMITENTE", bold: true },
    `Nome: ${proposta.nome}`,
    `CPF: ${proposta.cpfMasked}`,
    `Matricula: ${proposta.matricula}`,
    `Convenio: ${proposta.convenio}`,
    "",
    { text: "2. CONDICOES DA OPERACAO", bold: true },
    `Produto: ${PRODUTO_LABEL[proposta.produto]}`,
    `Valor financiado: ${fmtBRL(proposta.valor)}`,
    `Parcelas: ${proposta.parcelas}x de ${fmtBRL(proposta.parcela)}`,
    `Taxa: ${proposta.taxaAm.toFixed(2)}% a.m.`,
    "",
    { text: "3. CLAUSULAS GERAIS", bold: true },
    "3.1. O EMITENTE declara ter recebido copia integral desta CCB e das",
    "     condicoes gerais aplicaveis ao contrato de credito consignado.",
    "3.2. As parcelas serao descontadas diretamente da folha de pagamento",
    "     do EMITENTE junto ao convenio identificado no item 1.",
    "3.3. Em caso de rescisao do vinculo empregaticio, o saldo devedor",
    "     tornar-se-a exigivel conforme legislacao vigente.",
    "",
    { text: "4. ASSINATURAS", bold: true },
    "",
    "_______________________________________",
    `${proposta.nome} - EMITENTE`,
    `CPF: ${proposta.cpfMasked}`,
    "",
    "_______________________________________",
    "Consignatária / Representante legal",
    "",
    "_______________________________________",
    "Testemunha 1",
    "",
    "_______________________________________",
    "Testemunha 2",
    "",
    { text: "MODELO PARA DEMONSTRACAO - NAO E DOCUMENTO CONTRATUAL VALIDO.", bold: true },
  ]);
  downloadPdf(`contrato-modelo-${proposta.idUnico}.pdf`, bytes);
}

/**
 * O botão da CCB modelo.
 *
 * Aparece SÓ onde ainda não há contrato anexado, que é onde alguém ainda vai
 * colher assinatura — depois de anexado, o modelo não serve a nada e competiria
 * pela atenção com o arquivo de verdade.
 *
 * O rótulo diz **modelo**, e o `title` repete: o PDF se identifica como
 * demonstração ("NAO E DOCUMENTO CONTRATUAL VALIDO"), e um botão que prometesse
 * contrato válido seria pior que botão nenhum — foi por não decidir isto que a
 * função ficou escrita e sem chamador (item 4 do PENDENCIAS.md).
 */
function BaixarModeloCcb({ proposta }: { proposta: BancoProposta }) {
  return (
    <Button
      variant="ghost"
      onClick={() => baixarContratoModelo(proposta)}
      title="Gera um PDF com os dados desta proposta para imprimir e colher a assinatura presencialmente. É um modelo de demonstração — não substitui o contrato da consignatária."
    >
      Baixar CCB modelo
    </Button>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14 }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 500, fontFamily: mono ? "var(--font-mono)" : undefined, textAlign: "right" }}>{value}</span>
    </div>
  );
}

/** Card mostrando o contrato ja anexado — o operador pode reabrir a qualquer
 *  momento. Faz fetch autenticado (fetchCcbBlob) e abre em blob URL, senao
 *  daria 401 no endpoint (mesmo motivo do bug do comprovante). */
function CcbAnexadoView({ proposta, onAtualizar }: { proposta: BancoProposta; onAtualizar: () => void }) {
  const [abrindoKey, setAbrindoKey] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const abrir = async (key: string) => {
    setErro(null);
    setAbrindoKey(key);
    try {
      const blob = await atlas.banco.fetchCcbBlob(key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      setErro((e as Error).message || "Falha ao abrir o arquivo.");
    } finally {
      setAbrindoKey(null);
    }
  };
  const anexadoEm = proposta.ccbAnexadoEm
    ? new Date(proposta.ccbAnexadoEm).toLocaleString("pt-BR")
    : null;
  const nomeArq = proposta.ccbKey?.split("/").pop() ?? "contrato";
  const historico = (proposta.ccbHistorico ?? []).slice().reverse(); // mais recente primeiro
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Versao atual */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
        background: "color-mix(in srgb, var(--emerald-500) 8%, transparent)",
        border: "1px solid var(--emerald-500)", borderRadius: 10, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 20 }}>📄</span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>
            Contrato atual{historico.length > 0 ? ` · versão ${historico.length + 1}` : ""}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, wordBreak: "break-all" }}>
            {nomeArq}{anexadoEm ? ` · ${anexadoEm}` : ""}
          </div>
          {erro ? <div style={{ fontSize: 11, color: "var(--danger-500)", marginTop: 4 }}>{erro}</div> : null}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => proposta.ccbKey && abrir(proposta.ccbKey)}
          disabled={abrindoKey === proposta.ccbKey}
        >
          {abrindoKey === proposta.ccbKey ? "Abrindo..." : "Abrir arquivo"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onAtualizar}>Atualizar contrato</Button>
      </div>

      {/* Historico de versoes anteriores — NUNCA excluidas, so arquivadas. */}
      {historico.length > 0 ? (
        <details style={{
          padding: "8px 12px", borderRadius: 8, background: "var(--bg-elev-2)",
          border: "1px solid var(--border)", fontSize: 12,
        }}>
          <summary style={{ cursor: "pointer", color: "var(--text-muted)", userSelect: "none" }}>
            Versoes anteriores ({historico.length})
          </summary>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {historico.map((h, i) => {
              const nome = h.key.split("/").pop() ?? "contrato";
              const data = new Date(h.anexadoEm).toLocaleString("pt-BR");
              const versao = historico.length - i; // mais recente = versao maior
              return (
                <div key={h.key} style={{
                  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                  padding: "6px 8px", background: "var(--bg-elev)", borderRadius: 6,
                }}>
                  <span style={{ fontSize: 13 }}>📎</span>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>
                      Versão {versao} <span style={{ color: "var(--text-dim)", fontWeight: 400 }}>· arquivada em {data}</span>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-dim)", wordBreak: "break-all" }}>{nome}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => abrir(h.key)} disabled={abrindoKey === h.key}>
                    {abrindoKey === h.key ? "Abrindo..." : "Abrir"}
                  </Button>
                </div>
              );
            })}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/** Modal do fluxo "Anexar contrato" — simplesmente escolhe um arquivo PDF
 *  e sobe pro R2. Fluxo pedido pelo cliente: banco anexa antes de aprovar
 *  a proposta; quando ja tem contrato, o mesmo modal serve pra ATUALIZAR
 *  (versao antiga vai automaticamente pro ccbHistorico do backend, nunca
 *  e apagada). Sem opcao de "baixar modelo" — o banco tem o proprio modelo. */
function AnexarContratoModal({
  proposta,
  onClose,
  onUploaded,
}: {
  proposta: BancoProposta;
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    if (!arquivo) {
      setErro("Selecione o arquivo do contrato assinado antes de enviar.");
      return;
    }
    setErro(null);
    setEnviando(true);
    try {
      await atlas.banco.uploadCcb(proposta.idUnico, arquivo);
      onUploaded();
      onClose();
    } catch (e) {
      setErro((e as Error).message || "Falha ao enviar o arquivo.");
    } finally {
      setEnviando(false);
    }
  };

  // Whitelist rigida: PDF, DOCX e Excel (XLS/XLSX). Espelhada no backend em
  // apps/api/src/modules/portal-banco/index.ts. Alguns navegadores devolvem
  // type vazio pra DOCX/XLSX — por isso valida tambem pela extensao.
  const TIPOS_ACEITOS = new Set([
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ]);
  const EXT_ACEITAS = /\.(pdf|docx|xls|xlsx)$/i;
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    if (f) {
      const extOk = EXT_ACEITAS.test(f.name);
      if (f.type ? !TIPOS_ACEITOS.has(f.type) : !extOk) {
        setErro("Apenas arquivos PDF, DOCX ou Excel (XLS/XLSX).");
        setArquivo(null);
        return;
      }
    }
    setErro(null);
    setArquivo(f);
  };

  const jaTemContrato = !!proposta.ccbKey;
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget && !enviando) onClose(); }} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>{jaTemContrato ? "Atualizar contrato" : "Anexar contrato"}</h3>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          {proposta.nome} · <code>{proposta.idUnico}</code>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {jaTemContrato
            ? "Enviar uma nova versao arquiva a atual no historico — nada e excluido."
            : "Escolha o arquivo do contrato assinado (PDF, DOCX ou Excel). Depois de anexar voce pode aprovar a proposta."}
        </div>

        {/* Upload */}
        <div>
          <label style={{
            display: "flex", flexDirection: "column", gap: 8, padding: 20,
            border: "2px dashed var(--border-strong)", borderRadius: 12,
            background: "var(--bg-elev-2)", cursor: enviando ? "not-allowed" : "pointer",
            alignItems: "center", textAlign: "center", opacity: enviando ? 0.6 : 1,
          }}>
            <span style={{ fontSize: 24 }}>📄</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              {arquivo ? arquivo.name : "Clique para selecionar o arquivo (PDF, DOCX ou Excel)"}
            </span>
            {arquivo ? (
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {(arquivo.size / 1024).toFixed(0)} KB
              </span>
            ) : (
              <span style={{ fontSize: 12, color: "var(--text-dim)" }}>
                PDF, DOCX ou Excel (XLS/XLSX) · ou arraste e solte aqui
              </span>
            )}
            <input
              type="file"
              accept="application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,application/vnd.ms-excel,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
              style={{ display: "none" }}
              onChange={onFileChange}
              disabled={enviando}
            />
          </label>
        </div>

        {erro ? (
          <div style={{
            padding: "8px 12px", borderRadius: 8,
            background: "color-mix(in srgb, var(--danger-500) 10%, transparent)",
            border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--danger-500)",
          }}>
            {erro}
          </div>
        ) : null}

        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={enviando}>Voltar</Button>
          <Button type="button" disabled={!arquivo || enviando} onClick={enviar}>
            {enviando ? "Enviando..." : "Enviar contrato"}
          </Button>
        </FormActions>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 520, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  // Modal alto (form longo) precisa rolar dentro de si — sem isto o rodape
  // fica fora da viewport e nao da pra chegar nos botoes. Cliente 05/08/2026.
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};

/** Modal de EDITAR PROPOSTA — cliente pediu 30/07/2026. Fluxo de negociacao
 *  offline: banco liga pro servidor pra combinar valores diferentes; depois
 *  vem aqui e edita a proposta com os novos numeros. Ao APROVAR (fora deste
 *  modal), o servidor recebe notif com o que foi acordado. O proposta.valor,
 *  parcelas e taxa mostrados aqui sao os ATUAIS — apos a edicao, sao os novos. */
function EditarPropostaModal({
  proposta,
  onClose,
  onSalvo,
}: {
  proposta: BancoProposta;
  onClose: () => void;
  onSalvo: () => void;
}) {
  // Condicoes INFORMADAS pelo banco (30/09/2026): o banco digita financiado,
  // liquido, parcela, IOF, CET e numero do contrato como saem do sistema dele.
  // A Atlas nao recalcula nada — confere margem, teto de CET, faixa de prazo, e
  // AVISA quando a parcela nao condiz com o CET. Os campos abrem com o que a
  // proposta ja tem (e' uma edicao), nunca com conta feita pela tela.
  const [valor, setValor] = useState<number | null>(proposta.valor);
  const [parcelasTxt, setParcelasTxt] = useState<string>(String(proposta.parcelas));
  const [valorParcela, setValorParcela] = useState<number | null>(proposta.parcela > 0 ? proposta.parcela : null);
  /** Spec 014: emprestimo e portabilidade -> o campo de taxa e' o CET, com
   *  teto; juros viram informativos. Cartao informa juros do rotativo e CET. */
  const ehEmprestimo = proposta.produto === "emprestimo" || proposta.produto === "portabilidade";
  // Arredonda pra 4 casas — proposta.taxaAm vem como 1.5599999999998 (residuo de
  // 0.0156 * 100) e o campo cru mostrava esse lixo. Cliente 30/07.
  const taxaInicial = String(Number(proposta.taxaAm.toFixed(4)));
  const [cetTxt, setCetTxt] = useState<string>(ehEmprestimo ? taxaInicial : "");
  const [jurosTxt, setJurosTxt] = useState<string>(ehEmprestimo ? "" : taxaInicial);
  const [iof, setIof] = useState<number | null>(null);
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [numeroContratoBanco, setNumeroContratoBanco] = useState<string>(proposta.numeroContratoBanco ?? "");
  const [vencimento1aParcela, setVencimento1aParcela] = useState<string>("");
  const [dataLiberacaoCredito, setDataLiberacaoCredito] = useState<string>("");
  const [diasCarenciaTxt, setDiasCarenciaTxt] = useState<string>("");
  const [valorLiquido, setValorLiquido] = useState<number | null>(null);
  const [valorCarencia, setValorCarencia] = useState<number | null>(null);

  const num = (s: string): number | null => {
    const t = s.trim().replace(",", ".");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };
  const parcelas = num(parcelasTxt);
  const cetPct = num(cetTxt);
  const jurosPct = num(jurosTxt);
  // Vazio = nao informado: a edicao mantem a carencia que o contrato ja tem.
  const diasCarenciaInformado = num(diasCarenciaTxt);
  const diasCarencia = diasCarenciaInformado ?? 0;
  const cetAm = cetPct != null ? Number((cetPct / 100).toFixed(6)) : null;
  const jurosAm = jurosPct != null ? Number((jurosPct / 100).toFixed(6)) : null;

  // Teto de margem pra ESTA edicao: o que sobra na folha MAIS o que a propria
  // proposta ja ocupa (a parcela antiga sai pra dar lugar a nova). Backend
  // valida igual (422 margem_insuficiente). Cliente 05/08/2026.
  const tetoMargem = proposta.margemDisponivel + proposta.margemComprometida;
  const conveniosQ = useQuery({
    queryKey: ["banco", "convenios"],
    queryFn: () => atlas.banco.convenios(),
    staleTime: 5 * 60_000,
  });
  const cfgConvenio = conveniosQ.data?.convenios.find((cv) => cv.nome === proposta.convenio);
  const prazoMin = cfgConvenio?.prazoMin ?? 1;
  const prazoMax = cfgConvenio?.prazoMax ?? Math.min(cfgConvenio?.maxParcelas ?? 120, 120);
  /** Teto de CET da prefeitura, em %. null = nao configurado. Cartao nao passa
   *  por ele (spec 014). */
  const tetoCetPct = ehEmprestimo ? (cfgConvenio?.taxaMaxAm ?? null) : null;

  // Travas — as mesmas do backend.
  const excedeMargem = valorParcela != null && valorParcela > tetoMargem + 0.01;
  const prazoFora = parcelas != null && (parcelas < prazoMin || parcelas > prazoMax || !Number.isInteger(parcelas));
  const excedeCet = tetoCetPct != null && cetPct != null && cetPct > tetoCetPct + 1e-9;
  const cetAbaixoMinimo = ehEmprestimo && cetAm != null && cetAm > 0 && cetAm < CET_MINIMO_AM - 1e-9;
  const cetAbaixoJuros = ehEmprestimo && jurosPct != null && jurosPct > 0 && cetPct != null && cetPct < jurosPct - 1e-9;
  const liquidoAcimaDoFinanciado = valor != null && valorLiquido != null && valorLiquido > valor + 0.005;
  const valorTotalPagar = valorParcela != null && parcelas != null ? Math.round(valorParcela * parcelas * 100) / 100 : 0;
  // Aviso — nao trava.
  const avisoCoerencia = ehEmprestimo && valorParcela != null && parcelas != null && cetAm != null
    ? avisoCoerenciaParcela({ parcela: valorParcela, valorLiquido: valorLiquido ?? valor ?? 0, parcelas, cetAm, diasCarencia })
    : null;
  const faltaObrigatorio =
    valor == null || valor <= 0 || valorParcela == null || valorParcela <= 0 || parcelas == null || parcelas <= 0 ||
    (ehEmprestimo ? cetAm == null || cetAm <= 0 : jurosAm == null || jurosAm <= 0);

  const folha1oDesconto = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(vencimento1aParcela);
    return m ? `${m[2]}/${m[1]}` : "";
  }, [vencimento1aParcela]);

  const mut = useMutation({
    mutationFn: () => {
      if (valor == null || valorParcela == null || parcelas == null) throw new Error("Preencha os campos obrigatórios.");
      return atlas.banco.editarProposta(proposta.idUnico, {
        valor,
        parcelas,
        // Emprestimo: `taxaAm` e' o CET. Cartao: sao os juros, e o CET vai a parte.
        taxaAm: ehEmprestimo ? (cetAm ?? 0) : (jurosAm ?? 0),
        jurosAm: ehEmprestimo && jurosAm != null && jurosAm > 0 ? jurosAm : undefined,
        cetAm: !ehEmprestimo && cetAm != null && cetAm > 0 ? cetAm : undefined,
        valorParcela,
        iof: iof ?? undefined,
        motivo: motivo.trim() || undefined,
        numeroContratoBanco: numeroContratoBanco.trim() || undefined,
        vencimento1aParcela: vencimento1aParcela || undefined,
        dataLiberacaoCredito: dataLiberacaoCredito || undefined,
        valorLiquidoLiberado: valorLiquido ?? undefined,
        valorCarencia: valorCarencia ?? undefined,
        diasCarencia: diasCarenciaInformado ?? undefined,
      });
    },
    onSuccess: () => onSalvo(),
    onError: (err) => setErro(err instanceof ApiHttpError ? err.message : err instanceof Error ? err.message : "Falha ao salvar edicao"),
  });
  const fmtBRLUi = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const pctBr = (v: number) => v.toFixed(2).replace(".", ",");
  const caixaErro: React.CSSProperties = { padding: "10px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--text)", lineHeight: 1.5 };
  return (
    <div style={modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget && !mut.isPending) onClose(); }}>
      <div style={{ ...modalCard, maxWidth: 900 }} onClick={(e) => e.stopPropagation()}>
        {/* Mesmo destaque da ficha de averbacao (cliente 05/08/2026): os tres
            numeros que decidem a operacao ficam no topo, sempre visiveis. */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0 }}>Editar valores da proposta</h3>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
              {proposta.nome} · <code>{proposta.idUnico}</code>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
                Margem disponível
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: "var(--emerald-500)" }}>
                {fmtBRLUi(tetoMargem)}
              </div>
              <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>inclui a parcela atual</div>
            </div>
            <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: excedeMargem ? "color-mix(in srgb, var(--danger-500) 12%, transparent)" : "var(--bg-elev-2)", border: `1px solid ${excedeMargem ? "var(--danger-500)" : "var(--border)"}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
                Valor da parcela
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: excedeMargem ? "var(--danger-500)" : "var(--text)" }}>
                {valorParcela != null && valorParcela > 0 ? fmtBRLUi(valorParcela) : "—"}
              </div>
            </div>
            <div style={{ minWidth: 150, padding: "10px 14px", borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
                Total a pagar
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: "var(--text)" }}>
                {valorTotalPagar > 0 ? fmtBRLUi(valorTotalPagar) : "—"}
              </div>
              <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>
                {valorTotalPagar > 0 && valorParcela != null ? `${parcelas}× de ${fmtBRLUi(valorParcela)}` : " "}
              </div>
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
          Servidor solicitou <b>{fmtBRL(proposta.valor)}</b> em {proposta.parcelas}x @ {proposta.taxaAm.toFixed(2)}% a.m. Informe as condições acordadas como calculadas no seu sistema — ao aprovar em seguida, o servidor recebe notificação com o que foi acertado.
        </div>
        <FormGrid cols={3}>
          <TextField
            label="Nº do contrato no seu sistema"
            value={numeroContratoBanco}
            onChange={(e) => setNumeroContratoBanco(e.target.value)}
            maxLength={60}
            hint="O número do contrato no seu sistema — não pode repetir"
          />
          <TextField
            label="Vencimento 1ª parcela"
            type="date"
            value={vencimento1aParcela}
            onChange={(e) => setVencimento1aParcela(e.target.value)}
          />
          <TextField
            label="Folha 1º desconto"
            value={folha1oDesconto || "—/—"}
            readOnly
          />
          <CurrencyField
            label="Novo valor financiado *"
            value={valor}
            onValueChange={setValor}
          />
          <CurrencyField
            label="Valor líquido liberado"
            value={valorLiquido}
            onValueChange={setValorLiquido}
            hint="O que cai na conta do servidor"
            error={liquidoAcimaDoFinanciado ? "Maior que o financiado" : undefined}
          />
          <TextField
            label="Data de liberação do crédito"
            type="date"
            value={dataLiberacaoCredito}
            onChange={(e) => setDataLiberacaoCredito(e.target.value)}
          />
          <NumberField
            label={`Parcelas * (${prazoMin}× a ${prazoMax}×)`}
            step={1}
            min={prazoMin}
            max={prazoMax}
            value={parcelasTxt}
            onChange={(e) => setParcelasTxt(e.target.value)}
            error={prazoFora ? `Entre ${prazoMin}× e ${prazoMax}×` : undefined}
          />
          <CurrencyField
            label="Valor da parcela *"
            value={valorParcela}
            onValueChange={setValorParcela}
            hint="Calculada no seu sistema"
            error={excedeMargem ? "Acima da margem disponível" : undefined}
          />
          <CurrencyField
            label="Valor IOF"
            value={iof}
            onValueChange={setIof}
          />
          {ehEmprestimo ? (
            <>
              <NumberField
                label="CET (% a.m.) — conforme a CCB *"
                step={0.01}
                min={0}
                max={tetoCetPct ?? undefined}
                value={cetTxt}
                onChange={(e) => setCetTxt(e.target.value)}
                hint={`Custo Efetivo Total: juros + IOF + tarifas + seguro. Não é a taxa de juros.${tetoCetPct != null ? ` Teto da prefeitura: ${pctBr(tetoCetPct)}% a.m.` : ""}${cetAm != null && cetAm > 0 ? ` Equivale a ${pctBr(cetAnual(cetAm) * 100)}% a.a.` : ""}`}
                error={excedeCet ? "Acima do teto do órgão" : undefined}
              />
              <NumberField
                label="Juros mensal (%) — opcional"
                step={0.01}
                min={0}
                value={jurosTxt}
                onChange={(e) => setJurosTxt(e.target.value)}
                hint="A taxa de juros do seu sistema. Sem teto; o CET não pode ficar abaixo dela."
              />
            </>
          ) : (
            <>
              <NumberField
                label="Juros mensal (%) *"
                step={0.01}
                min={0}
                value={jurosTxt}
                onChange={(e) => setJurosTxt(e.target.value)}
                hint="Juros do rotativo. Cartão não passa pelo teto de CET do convênio."
              />
              <NumberField
                label="CET (% a.m.)"
                step={0.01}
                min={0}
                value={cetTxt}
                onChange={(e) => setCetTxt(e.target.value)}
              />
            </>
          )}
          <NumberField
            label="Dias de carência"
            step={1}
            min={0}
            value={diasCarenciaTxt}
            onChange={(e) => setDiasCarenciaTxt(e.target.value)}
          />
          <CurrencyField
            label="Valor carência"
            value={valorCarencia}
            onValueChange={setValorCarencia}
          />
        </FormGrid>
        <TextareaField
          label="Motivo / observação (opcional)"
          hint="Ex.: servidor concordou com valor menor + prazo maior por telefone em 03/08."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          maxLength={300}
        />
        {excedeCet && tetoCetPct != null && cetPct != null ? (
          <div style={caixaErro}>
            CET de <b>{pctBr(cetPct)}%</b> a.m. excede o teto de <b>{pctBr(tetoCetPct)}%</b> definido pela prefeitura para este convênio.
          </div>
        ) : cetAbaixoMinimo ? (
          <div style={caixaErro}>
            CET abaixo do mínimo de <b>{pctBr(CET_MINIMO_AM * 100)}%</b> a.m. Confira a digitação: o CET é informado em percentual (1,79 para 1,79%).
          </div>
        ) : cetAbaixoJuros ? (
          <div style={caixaErro}>O CET não pode ficar abaixo dos juros — ele os inclui. Confira se os dois campos não foram trocados.</div>
        ) : liquidoAcimaDoFinanciado ? (
          <div style={caixaErro}>O valor líquido liberado não pode ser maior que o valor financiado.</div>
        ) : prazoFora ? (
          <div style={caixaErro}>
            O prazo tem de ficar entre <b>{prazoMin}×</b> e <b>{prazoMax}×</b> — faixa das suas tabelas no convênio, limitada pelo teto do órgão.
          </div>
        ) : excedeMargem && valorParcela != null ? (
          <div style={caixaErro}>
            A parcela de <b>{fmtBRL(valorParcela)}</b> excede a margem disponível (<b>{fmtBRL(tetoMargem)}</b>).
          </div>
        ) : null}
        {avisoCoerencia && !excedeMargem ? (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", border: "1px solid var(--gold-500)", fontSize: 13, color: "var(--text)", lineHeight: 1.5 }}>
            A parcela informada difere em <b>{(avisoCoerencia.diferenca * 100).toFixed(1).replace(".", ",")}%</b> da parcela pelo CET
            informado (<b>{fmtBRLUi(avisoCoerencia.parcelaPeloCet)}</b>). Confira os números — a edição não é bloqueada por isso.
          </div>
        ) : null}
        {erro ? (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--danger-500)" }}>
            {erro}
          </div>
        ) : null}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={mut.isPending}>Cancelar</Button>
          <Button type="button" onClick={() => { setErro(null); mut.mutate(); }} disabled={mut.isPending || faltaObrigatorio || prazoFora || excedeCet || cetAbaixoMinimo || cetAbaixoJuros || liquidoAcimaDoFinanciado || excedeMargem}>
            {mut.isPending ? "Salvando..." : "Salvar edição →"}
          </Button>
        </FormActions>
      </div>
    </div>
  );
}
